/**
 * Marketing i18n fragment — securityPage (/security).
 *
 * The security landing page the nav's Security mega-menu deep-links into
 * (#tenant-isolation, #governance, #evidence, #auditability). Every claim
 * here is documented shipped behaviour (content/docs/en/admin/security.md,
 * the decision ledger, the outbox) — see
 * docs/ai/fixcontrol-website-claims.md before editing. "Audit-ready" is
 * permitted; naming compliance certifications is not, unless we hold them.
 */

export const securityPageEn = {
  meta: {
    title: "Security — tenant isolation, governance, evidence and audit",
    description:
      "How FixControl keeps AI-assisted operations safe: Postgres row-level security isolates every tenant, high-risk actions wait for a recorded human decision, each recommendation carries the evidence it rests on, and a hash-chained audit log exports in a signed envelope that proves the file was not altered after export.",
  },
  hero: {
    eyebrow: "Security",
    title: "Built so AI can act without",
    highlight: "anyone losing control",
    subtitle:
      "FixControl is a governance layer. The AI proposes a change; your policy and your people decide whether it goes ahead; and every decision is written to an audit trail you can replay. Isolation, approval and audit are built into how it works.",
    primaryCta: "Read the security documentation",
    secondaryCta: "Contact us",
    bullet1: "Tenant isolation enforced by Postgres RLS",
    bullet2: "Human approval by default",
    bullet3: "Hash-chained, append-only audit trail",
  },
  glance: {
    encryption: "AES-256-GCM secrets",
    rls: "Row-level security, strict mode",
    audit: "Hash-chained audit log",
    identity: "OIDC SSO + SCIM",
    twoPerson: "Two-person rule",
  },
  isolation: {
    eyebrow: "Tenant isolation",
    title: "Every customer operates in an isolated environment",
    description:
      "In plain terms: your data can never be seen by another organization using FixControl. Isolation is enforced in three layers: the request authenticates, the application filters to your own organization (its tenant), and Postgres row-level security has the final say — running in strict mode, which hosted production refuses to boot without. No single mistake takes the wall down.",
    items: {
      layers: {
        title: "Layered enforcement",
        body: "Authentication, tenant filtering and Postgres row-level security all have to agree before a row is returned. A query that arrives without a tenant context reads zero rows rather than someone else's data. Inside a tenant, project roles and memberships scope access further: project-bound work is deny-by-default for non-members.",
      },
      url: {
        title: "No cross-tenant reads — even by URL",
        body: "A user in another tenant can't open your issues, emails or codebases, even from a direct link, and knowledge search stops at the tenant boundary.",
      },
      secrets: {
        title: "Credentials scoped to your tenant",
        body: "Provider tokens and secrets are encrypted with AES-256-GCM and stored in rows only your tenant can read.",
      },
      staff: {
        title: "Staff access is audited",
        body: "A cross-tenant read by FixControl staff runs through the same isolation path, needs a written reason, and lands in your tenant's audit log.",
      },
    },
  },
  governance: {
    eyebrow: "Governance",
    title: "Human approval before high-risk actions",
    description:
      "By default, nothing ships without a recorded human decision. Where a tenant turns on more autonomy, that choice is explicit, bounded by policy and written to the audit log.",
    items: {
      approval: {
        title: "Approval gates on risky work",
        body: "A plan, a risky patch, a deployment or a customer-facing reply stops at a gate until someone approves it, rejects it or asks for changes.",
      },
      ledger: {
        title: "A frozen decision ledger",
        body: "Each verdict freezes who decided, in which role, over which evidence, under which policy version and through which channel, so a decision can't be quietly rewritten later.",
      },
      twoPerson: {
        title: "Two-person rule on irreversible operations and customer e-mail",
        body: "Five irreversible platform operations — purging a workspace, deleting a person across the platform, rotating the master key, dropping an audit partition, erasing a data subject — need a token one FixControl platform admin mints and a different one spends: single-use, short-lived, rate-limited and audited. Outbound customer e-mail follows the same four-eyes principle: the requester never approves their own message, and the approved content is frozen — any change invalidates the approval.",
      },
      outbox: {
        title: "No silent external writes",
        body: "A write to Slack, Jira or GitHub goes through a governed outbox that retries on failure and shows the failure when it can't recover. Nothing reaches an outside system without leaving a trail.",
      },
    },
    ladder: {
      title: "Three autonomy levels, each one opt-in",
      steps: {
        suggested: {
          tag: "Default",
          title: "Suggested only",
          body: "The AI prepares the work; an operator releases it. This is where every tenant starts.",
        },
        draft: {
          tag: "Opt-in",
          title: "Auto-draft",
          body: "The AI drafts the artefact ahead of time; an operator still releases it.",
        },
        auto: {
          tag: "Opt-in + guarded",
          title: "Auto-apply, policy-scoped",
          body: "Released without an operator click — off unless a tenant turns it on, bounded by policy and loud in the audit log. It never becomes the default for an existing tenant.",
        },
      },
    },
  },
  evidence: {
    eyebrow: "Evidence",
    title: "Every recommendation carries its evidence",
    description:
      "An approver shouldn't have to take the AI's word for anything. Each recommendation carries the evidence it rests on, and it is verified before the decision, not after it.",
    items: {
      explainable: {
        title: "Evidence behind every proposal",
        body: "A plan or patch cites the documentation, tickets and code it drew on, so the reviewer can see what it's based on and why.",
      },
      sandbox: {
        title: "Sandbox verification first",
        body: "A proposed change is verified in an isolated sandbox — a containerized environment with a disposable database, separate from your production systems; your deployment's configuration determines the exact isolation. Where configured, the supporting services the application needs are started alongside it, without internet access and without access to your source code, and removed after the run. A passed verification records the exact versions it passed against. The results travel into the approval before anything touches your repository.",
      },
      lineage: {
        title: "Evidence lineage in the timeline",
        body: "The mission timeline links each decision back to the evidence it was made on, from intake through to deployment.",
      },
      health: {
        title: "Capability-level integration health",
        body: "Connection status is tracked per capability, so a broken write path shows as broken instead of hiding behind one green checkmark.",
      },
    },
  },
  audit: {
    eyebrow: "Auditability",
    title: "Replay every operational decision",
    description:
      "In plain terms: when someone asks who decided what, and why, you answer from the record — not from memory. The audit log is hash-chained and append-only: each entry is hash-chained to the one before it in a single global sequence, and the database itself refuses updates and deletes on these tables outside an explicit, audited maintenance path. So an edit is prevented, not merely noticeable — and if one ever did land, every hash after it stops matching and the hourly chain verification reports the break.",
    items: {
      chain: {
        title: "Hash-chained, append-only",
        body: "Each audited event records the actor, the action, the target and the fields it changed. A database trigger refuses updates and deletes on the ledger tables outside an explicit maintenance path, and the hash chain makes any break visible — so history is protected in the database, not only checked afterwards.",
      },
      export: {
        title: "Signed audit export",
        body: "On Pro and Enterprise, your CSV export carries each entry's chain position and hashes inside a signed envelope, so a recipient with the export key can prove the file was not altered after export. On another plan the export refuses with a clear reason, and the refusal itself is recorded.",
      },
      replay: {
        title: "Mission replay",
        body: "A mission keeps its whole timeline — intake, triage, plan, verification, approvals, delivery — so you can reconstruct how any outcome happened.",
      },
      retention: {
        title: "365-day audit retention, with floors underneath",
        body: "Audit events are kept for a year on the hosted service, and a hard 90-day floor sits underneath that window so it can never be set short enough to shred what an incident review needs. The windows your own organisation sets — agent traces, approval evidence, runtime logs, customer communication — carry their own floors, and approval decisions and verification evidence are never deleted at all: what a window reaches is the free text and the payloads around them.",
      },
    },
  },
  limits: {
    eyebrow: "Deliberate limits",
    title: "What FixControl won't do, on purpose",
    description:
      "A few capabilities are left out deliberately. Leaving them out is what keeps the governance promise honest.",
    items: {
      automerge: {
        title: "No auto-merge",
        body: "A patch arrives as a pull or merge request; merging it stays a deliberate operator action.",
      },
      defaults: {
        title: "No silent autonomy",
        body: "A new automation level never switches itself on for an existing tenant. You opt in to it, under a policy you set.",
      },
      crossTenant: {
        title: "No cross-tenant analytics",
        body: "There is no shared-insights surface across tenants. A cross-tenant operation needs platform-admin scope and is audited.",
      },
      mfa: {
        title: "No 2FA back doors",
        body: "Disabling two-factor authentication requires the user's password plus a live factor, and the platform-admin emergency reset demands a written reason, is audited, revokes every session and notifies the user.",
      },
    },
  },
  practices: {
    eyebrow: "Platform practices",
    title: "The baseline underneath",
    description: "The uneventful parts, handled continuously.",
    items: {
      tls: {
        title: "TLS everywhere",
        body: "Certificates are managed automatically at the edge, and traffic stays encrypted in transit.",
      },
      secrets: {
        title: "Encrypted secrets",
        body: "Provider tokens and secrets are encrypted at rest with AES-256-GCM.",
      },
      sessions: {
        title: "Session control",
        body: "Sessions last 24 hours and can be revoked everywhere at once. Signing keys are rotated by FixControl as an explicit, audited operation — verification accepts more than one key, so a rotation never signs anyone out.",
      },
      identity: {
        title: "Enterprise identity",
        body: "OIDC SSO carries MFA from your identity provider, and SCIM 2.0 provisions and deprovisions accounts automatically.",
      },
      passwords: {
        title: "Password hygiene",
        body: "Local passwords are stored only as bcrypt hashes, so there is nothing to recover in plain text.",
      },
      webhooks: {
        title: "Signed webhooks",
        body: "An outbound webhook carries a timestamped HMAC-SHA256 signature, so the receiver can confirm it's genuine and not a replay.",
      },
    },
  },
  compliance: {
    eyebrow: "Compliance & assurance",
    title: "Where certification stands, honestly",
    description:
      "FixControl does not yet hold third-party certifications such as SOC 2 or ISO 27001, and we say that plainly rather than imply otherwise. What we offer today is direct evidence: control documentation, a reviewable audit chain, and access to the people who built the system.",
    items: {
      certifications: {
        title: "Third-party certifications — not yet",
        body: "Certification audits have not yet been completed, and we do not claim certifications until they are. Certification is on the roadmap; ask us for the current status. Architecture, controls and audit evidence can be reviewed directly during procurement or a pilot, under NDA where needed.",
      },
      pentest: {
        title: "External penetration test — not yet",
        body: "An external penetration test has not yet been performed. The codebase ships with automated security guards in CI — route authentication, tenant-isolation coverage, external-write coverage — and the architecture is open to review during procurement.",
      },
      disclosure: {
        title: "Responsible disclosure",
        body: "Found a vulnerability? Report it to contact@fixcontrol.ai — the address is published in /.well-known/security.txt. We confirm receipt and keep you informed while it is fixed.",
      },
      procurement: {
        title: "Procurement & security reviews",
        body: "Security questionnaires, an architecture overview and data-processing documentation are provided during onboarding and procurement review, answered directly by the team that builds the product.",
      },
      dpa: {
        title: "Data processing agreement (DPA)",
        body: "Where FixControl processes personal data on behalf of your organization, roles, subprocessors and deletion terms are recorded in a data processing agreement. The DPA is part of onboarding and available on request via contact@fixcontrol.ai.",
      },
      onboarding: {
        title: "Guided onboarding as a control",
        body: "Every rollout starts with guided onboarding that validates tenant boundaries, approval gates and audit behavior together with your team, on a scope you define. Governance is in place before the first automated action — a security control built into how FixControl goes live.",
      },
    },
  },
  platform: {
    eyebrow: "Hosting, data & AI providers",
    title: "Where your data lives and which models touch it",
    description:
      "Straight answers to the questions a security review asks first. The privacy statement carries the formal subprocessor list; this is the operational picture.",
    items: {
      hosting: {
        title: "Hosting & data residency",
        body: "The hosted service runs on infrastructure in the European Union (the Netherlands), operated for FixControl by TransIP, a Dutch hosting provider. Enterprise private deployments are scoped per engagement.",
      },
      aiProviders: {
        title: "AI model providers",
        body: "Anthropic is the default model provider. Which providers the hosted service uses is set by FixControl for the platform as a whole, not per workspace; within that, an organisation admin can pick a preferred model per AI agent. Embeddings can run on FixControl-managed infrastructure instead of a third party.",
      },
      training: {
        title: "No training on your data",
        body: "FixControl does not train models on customer data — there is no training pipeline in the product. Calls to model providers are inference requests governed by the provider's API terms; the defaults are named in the privacy statement's subprocessor list.",
      },
      subprocessors: {
        title: "Default subprocessors, named",
        body: "Three subprocessors underpin the core service: TransIP B.V. (hosting, the Netherlands), Anthropic PBC (AI models, the default provider) and Resend Inc. (transactional email). The formal, current list — including configuration-dependent providers — lives in the privacy statement.",
      },
      keys: {
        title: "Key management",
        body: "Integration credentials are encrypted at rest with AES-256-GCM under a per-tenant data key, wrapped by a master key ring that exists only on the server. Decryption happens server-side only and failed decrypts are audited. Keys are FixControl-managed; there is no customer-managed key option yet.",
      },
      accounts: {
        title: "Account security, plainly",
        body: "Self-serve accounts use passwords (bcrypt-hashed) with self-service reset and everywhere-at-once session revocation. Built-in TOTP two-factor authentication (authenticator app + recovery codes) can be enabled per user and required of everyone in your workspace. FixControl platform staff always need a 2FA-satisfied session; your own admins need one as soon as your workspace turns that policy on — being an admin is deliberately not a hardcoded floor, because that decision is yours. OIDC SSO sessions carry MFA from your identity provider instead.",
      },
      database: {
        title: "Customer database access",
        body: "The optional database connector is read-only (PostgreSQL), TLS-verified, restricted to allowlisted query shapes and approval-gated. Connections originate from FixControl's servers; concrete network details are shared during onboarding.",
      },
      privateInfra: {
        title: "Private infrastructure — outbound only",
        body: "Private Kubernetes, Argo, Jenkins and GitLab connect through the FC Agent: a service in your own environment that dials out to FixControl over HTTPS and executes signed, expiring, single-use operations. No inbound access, no VPN; your allowlists and least-privilege RBAC bound what it can do, and its credentials stay in your cluster. You install it from a signed public release and can verify the images before you run them.",
      },
      backups: {
        title: "Backups & continuity",
        body: "Databases are backed up automatically every day. No contractual RPO/RTO or availability commitment is published yet; Enterprise agreements can include committed terms.",
      },
      deletion: {
        title: "Deletion & export",
        body: "Cancelling soft-deletes your tenant with a restore window, after which an irreversible purge removes the data. Until then your audit history stays exportable as a signed CSV on Pro and Enterprise.",
      },
    },
  },
  boundaries: {
    eyebrow: "AI boundaries",
    title: "What the AI may do on its own — and what it may not",
    description:
      "The clearest security statement we can make is the boundary itself: what runs without a decision, what waits for one, and what we refuse to promise.",
    groups: {
      auto: {
        tag: "Without approval",
        title: "AI may do this on its own",
        items: {
          classify: "Classify and route incoming work",
          answer: "Answer ticket questions in authenticated Slack, Teams and in-app chat",
          draft: "Draft replies, plans and proposed changes",
          analyze: "Analyze code and prepare patches for review",
          checks: "Run configured read-only checks in the sandbox",
          environment: "Propose a sandbox environment for a repository it cannot detect deterministically — a hypothesis only: policy checks it and the sandbox has to prove it by really running it before it becomes reusable configuration",
        },
      },
      gated: {
        tag: "Requires approval",
        title: "These wait for a recorded decision",
        items: {
          replies: "Customer-visible replies (Freshdesk; Jira Service Management where configured)",
          tracker: "Comments and status changes on external trackers",
          pr: "Opening pull or merge requests",
          deploy: "Deployment promotions and CI/CD deployment verdicts on configured gates (Argo, GitHub Actions, GitLab, Jenkins)",
          writes: "Any other external write, as set by your policy",
        },
      },
      never: {
        tag: "Not promised",
        title: "What FixControl does not claim",
        items: {
          hallucination: "Hallucination-free AI — evidence and approval gates exist precisely because models can be wrong",
          replacement: "Replacing human responsibility — approvers remain accountable for what they approve",
          liability: "Legal or compliance liability transfer — FixControl provides governance evidence; contractual terms live in the agreement",
        },
      },
    },
  },
  faqTitle: "Security FAQ",
  faq: {
    q1: {
      q: "Can the AI ship a change without anyone approving it?",
      a: "By default, no — nothing ships without a recorded human decision. Where a tenant enables more autonomy, that choice is explicit, bounded by policy and written loudly to the audit log.",
    },
    q2: {
      q: "Will my customers see that I use FixControl?",
      a: "No. Replies go out from your own support address, and there is no “Powered by” badge.",
    },
    q3: {
      q: "Which FixControl staff can access my data?",
      a: "A small support team, only for explicit incident investigation and after you approve it. Every staff access is written to your tenant's audit log.",
    },
    q4: {
      q: "Is the audit log resistant to insider tampering?",
      a: "Every entry is hash-chained to the one before it in a single global sequence, and the database itself refuses updates and deletes on the audit tables outside an explicit maintenance path. A break is therefore both prevented and detectable: verification runs hourly and the audit screen shows the result. The signed export proves the file was not altered after export — that is file integrity, not independent third-party verification of the chain.",
    },
    q5: {
      q: "How do I enforce MFA?",
      a: "Built-in TOTP 2FA (authenticator app + recovery codes) can be required for everyone in your workspace. FixControl platform staff always need a 2FA-satisfied session; your own admins need one as soon as you turn that policy on — being an admin is deliberately not a hardcoded floor, because the decision is yours. With OIDC SSO, MFA comes from your identity provider — Okta, Azure AD or Google Workspace — and is not double-required.",
    },
    q6: {
      q: "Are you SOC 2 or ISO 27001 certified?",
      a: "Not yet. We do not imply certifications we do not hold. Architecture, controls and audit evidence can be reviewed directly during procurement or a pilot, and procurement documentation is provided during onboarding.",
    },
    q7: {
      q: "Where is my data hosted?",
      a: "On infrastructure in the European Union (the Netherlands). The privacy statement lists the subprocessors; Enterprise private deployments are scoped per engagement.",
    },
    q8: {
      q: "Is my data used to train AI models?",
      a: "FixControl does not train models on your data. Requests to model providers are inference calls under the provider's API terms, and which provider handles them follows your tenant's configuration.",
    },
    q9: {
      q: "Can FixControl scale with us?",
      a: "Yes. FixControl is designed as a scalable multi-tenant SaaS platform and can expand across seats, integrations, ticket volume and governed engineering workflows. Rollout starts with guided onboarding because the platform connects to sensitive operational systems; approval models, integration health and audit boundaries are validated with your team, and usage expands from there.",
    },
  },
  related: {
    agent: {
      label: "FC Agent — private infrastructure",
      description: "Outbound-only connectivity for private Kubernetes, Argo, Jenkins and GitLab.",
    },
    validation: {
      label: "Change validation",
      description: "Isolated Docker validation and validation in your own environment.",
    },
    deployment: {
      label: "Deployment governance",
      description: "How a paused deployment becomes one recorded decision.",
    },
  },
  cta: {
    title: "Security questions deserve real answers",
    description:
      "The full security documentation walks through isolation, identity, the audit chain, incident response and the deliberate limits in detail.",
    primary: "Read the security documentation",
    secondary: "Contact us",
  },
};

export const securityPageNl: typeof securityPageEn = {
  meta: {
    title: "Security — tenantisolatie, governance, bewijs en audit",
    description:
      "Hoe FixControl AI-ondersteunde operaties veilig houdt: Postgres row-level security isoleert elke tenant, risicovolle acties wachten op een vastgelegde menselijke beslissing, elke aanbeveling draagt het bewijs waarop ze rust, en een hash-chained auditlog exporteert in een ondertekende envelop die bewijst dat het bestand na export niet is gewijzigd.",
  },
  hero: {
    eyebrow: "Security",
    title: "Gebouwd zodat AI kan handelen zonder dat",
    highlight: "iemand de controle verliest",
    subtitle:
      "FixControl is een governance-laag. De AI stelt een wijziging voor; jouw beleid en jouw mensen beslissen of die doorgaat; en elke beslissing wordt vastgelegd in een audittrail die je kunt terugspelen. Isolatie, goedkeuring en audit zijn ingebouwd in hoe het werkt.",
    primaryCta: "Lees de security-documentatie",
    secondaryCta: "Neem contact op",
    bullet1: "Tenantisolatie afgedwongen door Postgres RLS",
    bullet2: "Menselijke goedkeuring standaard",
    bullet3: "Hash-geketende, append-only audittrail",
  },
  glance: {
    encryption: "AES-256-GCM secrets",
    rls: "Row-level security, strict mode",
    audit: "Hash-chained auditlog",
    identity: "OIDC SSO + SCIM",
    twoPerson: "Vier-ogen-principe",
  },
  isolation: {
    eyebrow: "Tenantisolatie",
    title: "Elke klant draait in een geïsoleerde omgeving",
    description:
      "In gewone taal: jouw data is nooit zichtbaar voor een andere organisatie die FixControl gebruikt. Isolatie wordt in drie lagen afgedwongen: het verzoek authenticeert, de applicatie filtert op jouw eigen organisatie (haar tenant), en Postgres row-level security heeft het laatste woord — in strict mode, waarzonder hosted productie weigert op te starten. Geen enkele fout haalt de muur omlaag.",
    items: {
      layers: {
        title: "Gelaagde handhaving",
        body: "Authenticatie, tenantfiltering en Postgres row-level security moeten het alle drie eens zijn voordat een rij wordt teruggegeven. Een query die zonder tenant-context binnenkomt, leest nul rijen in plaats van andermans data. Binnen een tenant perken projectrollen en -lidmaatschappen de toegang verder in: projectgebonden werk is deny-by-default voor niet-leden.",
      },
      url: {
        title: "Geen cross-tenant reads — ook niet via URL",
        body: "Een gebruiker in een andere tenant kan jouw issues, e-mails of codebases niet openen, ook niet via een directe link, en kennis-zoeken stopt bij de tenant-grens.",
      },
      secrets: {
        title: "Credentials beperkt tot jouw tenant",
        body: "Provider-tokens en secrets zijn versleuteld met AES-256-GCM en opgeslagen in rijen die alleen jouw tenant kan lezen.",
      },
      staff: {
        title: "Toegang door medewerkers wordt ge-audit",
        body: "Een cross-tenant read door FixControl-medewerkers loopt via hetzelfde isolatiepad, vereist een schriftelijke reden en belandt in jóuw auditlog.",
      },
    },
  },
  governance: {
    eyebrow: "Governance",
    title: "Menselijke goedkeuring vóór risicovolle acties",
    description:
      "Standaard gaat er niets de deur uit zonder een vastgelegde menselijke beslissing. Waar een tenant meer autonomie inschakelt, is die keuze expliciet, begrensd door beleid en vastgelegd in de auditlog.",
    items: {
      approval: {
        title: "Goedkeuringspoorten op risicovol werk",
        body: "Een plan, een risicovolle patch, een deployment of een klantgerichte reply stopt bij een poort totdat iemand het goedkeurt, afwijst of om wijzigingen vraagt.",
      },
      ledger: {
        title: "Een bevroren beslissingsgrootboek",
        body: "Elk oordeel bevriest wie besliste, in welke rol, over welk bewijs, onder welke beleidsversie en via welk kanaal, zodat een beslissing later niet stilletjes te herschrijven is.",
      },
      twoPerson: {
        title: "Vier-ogen-principe bij onomkeerbare operaties en klantmail",
        body: "Vijf onomkeerbare platformoperaties — een workspace definitief opruimen, iemand platformbreed verwijderen, de hoofdsleutel roteren, een auditpartitie droppen, een betrokkene wissen — vereisen een token dat de ene FixControl-platformbeheerder aanmaakt en een andere gebruikt: eenmalig, kort geldig, rate-limited en ge-audit. Uitgaande klantmail volgt hetzelfde vier-ogen-principe: de aanvrager keurt nooit zijn eigen bericht goed, en de goedgekeurde inhoud ligt vast — elke wijziging maakt de goedkeuring ongeldig.",
      },
      outbox: {
        title: "Geen stille externe writes",
        body: "Een write naar Slack, Jira of GitHub loopt via een governed outbox die opnieuw probeert bij een fout en de fout toont als het niet lukt. Niets bereikt een extern systeem zonder een spoor achter te laten.",
      },
    },
    ladder: {
      title: "Drie autonomieniveaus, elk opt-in",
      steps: {
        suggested: {
          tag: "Standaard",
          title: "Alleen voorstellen",
          body: "De AI bereidt het werk voor; een operator geeft het vrij. Hier begint elke tenant.",
        },
        draft: {
          tag: "Opt-in",
          title: "Auto-draft",
          body: "De AI stelt het artefact alvast op; een operator geeft het nog steeds vrij.",
        },
        auto: {
          tag: "Opt-in + bewaakt",
          title: "Auto-apply, beleidsgebonden",
          body: "Vrijgegeven zonder operatorklik — uit tenzij een tenant het aanzet, begrensd door beleid en luid in de auditlog. Het wordt nooit de standaard voor een bestaande tenant.",
        },
      },
    },
  },
  evidence: {
    eyebrow: "Bewijs",
    title: "Elke aanbeveling draagt haar bewijs mee",
    description:
      "Een goedkeurder hoeft de AI nergens op zijn woord te geloven. Elke aanbeveling draagt het bewijs waarop ze rust, en de verificatie gebeurt vóór de beslissing, niet erna.",
    items: {
      explainable: {
        title: "Bewijs achter elk voorstel",
        body: "Een plan of patch verwijst naar de documentatie, tickets en code waarop het steunt, zodat de reviewer ziet waarop het is gebaseerd en waarom.",
      },
      sandbox: {
        title: "Eerst sandbox-verificatie",
        body: "Een voorgestelde wijziging wordt geverifieerd in een geïsoleerde sandbox — een gecontaineriseerde omgeving met een wegwerpdatabase, gescheiden van je productiesystemen; de configuratie van je deployment bepaalt de exacte isolatie. Waar geconfigureerd draaien de ondersteunende services die de applicatie nodig heeft ernaast, zonder internettoegang en zonder toegang tot je broncode, en ze worden na de run opgeruimd. Een geslaagde verificatie legt vast tegen welke exacte versies hij slaagde. De resultaten reizen mee de goedkeuring in, vóór er iets je repository raakt.",
      },
      lineage: {
        title: "Bewijs-lineage in de tijdlijn",
        body: "De missietijdlijn koppelt elke beslissing terug aan het bewijs waarop die is genomen, van intake tot en met deployment.",
      },
      health: {
        title: "Capability-health per integratie",
        body: "Verbindingsstatus wordt per capability bijgehouden, zodat een kapot write-pad ook als kapot verschijnt in plaats van te verdwijnen achter één groen vinkje.",
      },
    },
  },
  audit: {
    eyebrow: "Auditeerbaarheid",
    title: "Speel elke operationele beslissing terug",
    description:
      "In gewone taal: als iemand vraagt wie wat besloot, en waarom, antwoord je vanuit de registratie — niet uit het geheugen. De auditlog is hash-geketend en append-only: elke regel is via een hash aan de vorige geketend in één globale reeks, en de database zelf weigert updates en deletes op deze tabellen buiten een expliciet, ge-audit onderhoudspad. Een wijziging wordt dus voorkomen, niet alleen opgemerkt — en zou er ooit toch één landen, dan klopt elke hash erna niet meer en meldt de ketenverificatie die elk uur loopt de breuk.",
    items: {
      chain: {
        title: "Hash-geketend, append-only",
        body: "Elk ge-audit event legt de actor, de actie, het doel en de gewijzigde velden vast. Een databasetrigger weigert updates en deletes op de ledger-tabellen buiten een expliciet onderhoudspad, en de hash-keten maakt elke breuk zichtbaar — de geschiedenis is dus beschermd in de database, niet alleen achteraf gecontroleerd.",
      },
      export: {
        title: "Ondertekende audit-export",
        body: "Op Pro en Enterprise bevat je CSV-export per regel de ketenpositie en de hashes, in een ondertekende envelop, zodat een ontvanger met de exportsleutel kan aantonen dat het bestand na export niet is gewijzigd. Op een ander plan weigert de export met een duidelijke reden, en die weigering wordt zelf vastgelegd.",
      },
      replay: {
        title: "Missie-replay",
        body: "Een missie behoudt zijn volledige tijdlijn — intake, triage, plan, verificatie, goedkeuringen, oplevering — zodat je kunt reconstrueren hoe elke uitkomst tot stand kwam.",
      },
      retention: {
        title: "365 dagen auditretentie, met ondergrenzen eronder",
        body: "Audit-events worden op de hosted dienst een jaar bewaard, met daaronder een harde ondergrens van 90 dagen, zodat dat venster nooit kort genoeg te zetten is om weg te vagen wat een incidentonderzoek nodig heeft. De termijnen die je eigen organisatie zet — agent-traces, goedkeuringsbewijs, runtime-logs, klantcommunicatie — hebben elk hun eigen ondergrens, en goedkeuringsbeslissingen en verificatiebewijs worden helemaal nooit verwijderd: wat een termijn raakt is de vrije tekst en de payloads eromheen.",
      },
    },
  },
  limits: {
    eyebrow: "Bewuste grenzen",
    title: "Wat FixControl bewust niet doet",
    description:
      "Een paar mogelijkheden ontbreken met opzet. Juist dat weglaten houdt de governance-belofte eerlijk.",
    items: {
      automerge: {
        title: "Geen auto-merge",
        body: "Een patch komt binnen als pull- of merge-request; het mergen blijft een bewuste operator-actie.",
      },
      defaults: {
        title: "Geen stille autonomie",
        body: "Een nieuw automatiseringsniveau gaat nooit vanzelf aan voor een bestaande tenant. Je kiest er zelf voor, onder een policy die jij instelt.",
      },
      crossTenant: {
        title: "Geen cross-tenant analytics",
        body: "Er is geen shared-insights-laag over tenants heen. Een cross-tenant operatie vereist platform-admin-scope en wordt ge-audit.",
      },
      mfa: {
        title: "Geen 2FA-achterdeurtjes",
        body: "Twee-factor-authenticatie uitschakelen vereist het wachtwoord van de gebruiker plus een geldige factor, en de emergency reset door een platform-admin vereist een schriftelijke reden, wordt geaudit, trekt alle sessies in en informeert de gebruiker.",
      },
    },
  },
  practices: {
    eyebrow: "Platformpraktijken",
    title: "De basis eronder",
    description: "De onopvallende onderdelen, continu geregeld.",
    items: {
      tls: {
        title: "Overal TLS",
        body: "Certificaten worden automatisch beheerd aan de edge, en verkeer blijft onderweg versleuteld.",
      },
      secrets: {
        title: "Versleutelde secrets",
        body: "Provider-tokens en secrets zijn at rest versleuteld met AES-256-GCM.",
      },
      sessions: {
        title: "Sessiebeheer",
        body: "Sessies duren 24 uur en kunnen in één keer overal worden ingetrokken. Ondertekeningssleutels worden door FixControl geroteerd als een expliciete, ge-audite handeling — verificatie accepteert meerdere sleutels, dus een rotatie logt niemand uit.",
      },
      identity: {
        title: "Enterprise-identiteit",
        body: "OIDC SSO neemt MFA over van je identityprovider, en SCIM 2.0 richt accounts automatisch in en weer uit.",
      },
      passwords: {
        title: "Wachtwoordhygiëne",
        body: "Lokale wachtwoorden worden alleen als bcrypt-hash opgeslagen, dus er valt niets in plain text terug te halen.",
      },
      webhooks: {
        title: "Ondertekende webhooks",
        body: "Een uitgaande webhook draagt een HMAC-SHA256-handtekening met tijdstempel, zodat de ontvanger kan controleren dat hij echt is en geen replay.",
      },
    },
  },
  compliance: {
    eyebrow: "Compliance & assurance",
    title: "Waar certificering staat, eerlijk gezegd",
    description:
      "FixControl heeft nog geen externe certificeringen zoals SOC 2 of ISO 27001, en dat zeggen we liever hardop dan het te suggereren. Wat we vandaag bieden is direct bewijs: control-documentatie, een naloopbare auditketen en toegang tot de mensen die het systeem hebben gebouwd.",
    items: {
      certifications: {
        title: "Externe certificeringen — nog niet",
        body: "Certificeringsaudits zijn nog niet uitgevoerd, en we claimen geen certificeringen totdat ze er zijn. Certificering staat op de roadmap; vraag ons naar de actuele status. Architectuur, controls en auditbewijs kunnen tijdens procurement of een pilot rechtstreeks worden beoordeeld, waar nodig onder NDA.",
      },
      pentest: {
        title: "Externe pentest — nog niet",
        body: "Een externe penetratietest is nog niet uitgevoerd. De codebase bevat geautomatiseerde security-guards in CI — route-authenticatie, tenant-isolatiedekking, dekking op externe writes — en de architectuur is tijdens procurement open voor review.",
      },
      disclosure: {
        title: "Responsible disclosure",
        body: "Kwetsbaarheid gevonden? Meld het via contact@fixcontrol.ai — het adres staat in /.well-known/security.txt. We bevestigen ontvangst en houden je op de hoogte tot het is opgelost.",
      },
      procurement: {
        title: "Procurement & security-reviews",
        body: "Security-vragenlijsten, een architectuuroverzicht en documentatie over gegevensverwerking worden tijdens onboarding en procurement-review aangeleverd, rechtstreeks beantwoord door het team dat het product bouwt.",
      },
      dpa: {
        title: "Verwerkersovereenkomst (DPA)",
        body: "Waar FixControl persoonsgegevens namens jouw organisatie verwerkt, worden rollen, subverwerkers en verwijdertermijnen vastgelegd in een verwerkersovereenkomst. De DPA is onderdeel van onboarding en op aanvraag beschikbaar via contact@fixcontrol.ai.",
      },
      onboarding: {
        title: "Begeleide onboarding als control",
        body: "Elke uitrol start met begeleide onboarding die tenant boundaries, approval gates en auditgedrag samen met je team valideert, op een scope die jij bepaalt. Governance staat vóór de eerste geautomatiseerde actie — een security-control die in de livegang van FixControl is ingebouwd.",
      },
    },
  },
  platform: {
    eyebrow: "Hosting, data & AI-providers",
    title: "Waar je data staat en welke modellen haar raken",
    description:
      "Rechte antwoorden op de vragen waar een security-review mee begint. De privacyverklaring bevat de formele subverwerkerslijst; dit is het operationele beeld.",
    items: {
      hosting: {
        title: "Hosting & dataresidentie",
        body: "De hosted dienst draait op infrastructuur in de Europese Unie (Nederland), voor FixControl beheerd door TransIP, een Nederlandse hostingprovider. Enterprise private deployments worden per engagement afgebakend.",
      },
      aiProviders: {
        title: "AI-modelproviders",
        body: "Anthropic is de standaard modelprovider. Welke providers de gehoste dienst gebruikt, stelt FixControl in voor het platform als geheel, niet per workspace; daarbinnen kan een beheerder van je organisatie per AI-agent een voorkeursmodel kiezen. Embeddings kunnen op door FixControl beheerde infrastructuur draaien in plaats van bij een derde partij.",
      },
      subprocessors: {
        title: "Standaard-subverwerkers, bij naam",
        body: "Drie subverwerkers dragen de kerndienst: TransIP B.V. (hosting, Nederland), Anthropic PBC (AI-modellen, de standaardprovider) en Resend Inc. (transactionele e-mail). De formele, actuele lijst — inclusief configuratie-afhankelijke providers — staat in de privacyverklaring.",
      },
      training: {
        title: "Geen training op jouw data",
        body: "FixControl traint geen modellen op klantdata — er zit geen trainingspipeline in het product. Aanroepen naar modelproviders zijn inference-verzoeken onder de API-voorwaarden van de provider; de standaardproviders staan in de subverwerkerslijst van de privacyverklaring.",
      },
      keys: {
        title: "Sleutelbeheer",
        body: "Integratie-credentials zijn at rest versleuteld met AES-256-GCM onder een data-sleutel per tenant, gewrapt door een master-keyring die alleen op de server bestaat. Ontsleutelen gebeurt uitsluitend server-side en mislukte ontsleutelingen worden geaudit. Sleutels worden door FixControl beheerd; een customer-managed-key-optie is er nog niet.",
      },
      accounts: {
        title: "Accountbeveiliging, zonder omhaal",
        body: "Self-serve accounts gebruiken wachtwoorden (bcrypt-gehasht) met self-service reset en sessie-intrekking overal tegelijk. Ingebouwde TOTP-twee-factor-authenticatie (authenticator-app + herstelcodes) kan per gebruiker worden aangezet en voor iedereen in je workspace worden verplicht. FixControl-platformmedewerkers hebben altijd een 2FA-bevestigde sessie nodig; je eigen admins zodra je workspace dat beleid aanzet — admin zijn is bewust geen vaste ondergrens, want die keuze is aan jou. OIDC SSO-sessies nemen MFA over van je identityprovider.",
      },
      database: {
        title: "Toegang tot klantdatabases",
        body: "De optionele databaseconnector is read-only (PostgreSQL), TLS-geverifieerd, beperkt tot allowlisted query-vormen en goedkeuringsgebonden. Verbindingen komen vanaf de servers van FixControl; concrete netwerkdetails worden tijdens onboarding gedeeld.",
      },
      privateInfra: {
        title: "Private infrastructuur — alleen uitgaand",
        body: "Private Kubernetes, Argo, Jenkins en GitLab koppelen via de FC Agent: een service in je eigen omgeving die uitgaand naar FixControl belt over HTTPS en getekende, aflopende, single-use operaties uitvoert. Geen inkomende toegang, geen VPN; jouw allowlists en least-privilege RBAC begrenzen wat hij kan, en zijn credentials blijven in je cluster. Je installeert hem vanuit een ondertekende openbare release en kunt de images verifiëren voor je ze draait.",
      },
      backups: {
        title: "Back-ups & continuïteit",
        body: "Databases worden dagelijks automatisch geback-upt. Er is nog geen contractuele RPO/RTO- of beschikbaarheidstoezegging gepubliceerd; Enterprise-overeenkomsten kunnen vaste afspraken bevatten.",
      },
      deletion: {
        title: "Verwijdering & export",
        body: "Bij opzegging wordt je tenant soft-deleted met een herstelvenster, waarna een onomkeerbare purge de data verwijdert. Tot dat moment blijft je audithistorie op Pro en Enterprise exporteerbaar als ondertekende CSV.",
      },
    },
  },
  boundaries: {
    eyebrow: "AI-grenzen",
    title: "Wat de AI zelfstandig mag doen — en wat niet",
    description:
      "De duidelijkste security-uitspraak is de grens zelf: wat zonder beslissing draait, wat erop wacht, en wat we weigeren te beloven.",
    groups: {
      auto: {
        tag: "Zonder goedkeuring",
        title: "Dit mag de AI zelfstandig",
        items: {
          classify: "Binnenkomend werk classificeren en routeren",
          answer: "Ticketvragen beantwoorden in geauthenticeerd Slack, Teams en in-app chat",
          draft: "Antwoorden, plannen en voorgestelde wijzigingen opstellen",
          analyze: "Code analyseren en patches voorbereiden voor review",
          checks: "Geconfigureerde read-only checks draaien in de sandbox",
          environment: "Een sandbox-omgeving voorstellen voor een repository die het niet deterministisch kan herkennen — alleen een hypothese: beleid controleert die en de sandbox moet het echt draaien voordat het herbruikbare configuratie wordt",
        },
      },
      gated: {
        tag: "Vereist goedkeuring",
        title: "Dit wacht op een vastgelegde beslissing",
        items: {
          replies: "Klantzichtbare antwoorden (Freshdesk; Jira Service Management waar geconfigureerd)",
          tracker: "Comments en statuswijzigingen op externe trackers",
          pr: "Pull- of merge-requests openen",
          deploy: "Deployment-promoties en CI/CD-deployment-verdicts op geconfigureerde poorten (Argo, GitHub Actions, GitLab, Jenkins)",
          writes: "Elke andere externe write, zoals jouw beleid het bepaalt",
        },
      },
      never: {
        tag: "Niet beloofd",
        title: "Wat FixControl niet claimt",
        items: {
          hallucination: "Hallucinatievrije AI — bewijs en goedkeuringspoorten bestaan juist omdat modellen fout kunnen zitten",
          replacement: "Menselijke verantwoordelijkheid vervangen — goedkeurders blijven aanspreekbaar op wat ze goedkeuren",
          liability: "Overdracht van juridische of compliance-aansprakelijkheid — FixControl levert governance-bewijs; contractuele voorwaarden staan in de overeenkomst",
        },
      },
    },
  },
  faqTitle: "Security-FAQ",
  faq: {
    q1: {
      q: "Kan de AI een wijziging uitleveren zonder dat iemand goedkeurt?",
      a: "Standaard niet — er gaat niets de deur uit zonder een vastgelegde menselijke beslissing. Waar een tenant meer autonomie aanzet, is die keuze expliciet, begrensd door beleid en luid vastgelegd in de auditlog.",
    },
    q2: {
      q: "Zien mijn klanten dat ik FixControl gebruik?",
      a: "Nee. Replies vertrekken vanaf je eigen supportadres en er is geen 'Powered by'-badge.",
    },
    q3: {
      q: "Welke FixControl-medewerkers kunnen bij mijn data?",
      a: "Een klein supportteam, alleen voor expliciet incidentonderzoek en nadat jij het hebt goedgekeurd. Elke medewerkerstoegang wordt vastgelegd in de auditlog van jouw tenant.",
    },
    q4: {
      q: "Is de auditlog bestand tegen manipulatie van binnenuit?",
      a: "Elke regel is via een hash aan de vorige geketend in één globale reeks, en de database weigert zelf updates en verwijderingen op de audittabellen buiten een expliciet onderhoudspad. Een breuk wordt dus zowel voorkomen als opgemerkt: de verificatie loopt elk uur en het auditscherm toont de uitkomst. De ondertekende export bewijst dat het bestand na export niet is gewijzigd — dat is bestandsintegriteit, geen onafhankelijke verificatie van de keten door een derde partij.",
    },
    q5: {
      q: "Hoe dwing ik MFA af?",
      a: "Ingebouwde TOTP-2FA (authenticator-app + herstelcodes) kan voor iedereen in je workspace worden verplicht. FixControl-platformmedewerkers hebben altijd een 2FA-bevestigde sessie nodig; je eigen admins zodra je dat beleid aanzet — admin zijn is bewust geen vaste ondergrens, want die keuze is aan jou. Met OIDC SSO komt MFA van je identityprovider — Okta, Azure AD of Google Workspace — en wordt ze niet dubbel vereist.",
    },
    q6: {
      q: "Zijn jullie SOC 2- of ISO 27001-gecertificeerd?",
      a: "Nog niet. We suggereren geen certificeringen die we niet hebben. Architectuur, controls en auditbewijs kunnen tijdens procurement of een pilot rechtstreeks worden beoordeeld, en procurement-documentatie wordt tijdens onboarding aangeleverd.",
    },
    q7: {
      q: "Waar wordt mijn data gehost?",
      a: "Op infrastructuur in de Europese Unie (Nederland). De privacyverklaring bevat de subverwerkers; Enterprise private deployments worden per engagement afgebakend.",
    },
    q8: {
      q: "Wordt mijn data gebruikt om AI-modellen te trainen?",
      a: "FixControl traint geen modellen op jouw data. Verzoeken aan modelproviders zijn inference-aanroepen onder de API-voorwaarden van de provider, en welke provider ze afhandelt volgt de configuratie van jouw tenant.",
    },
    q9: {
      q: "Kan FixControl met ons meegroeien?",
      a: "Ja. FixControl is ontworpen als schaalbaar multi-tenant SaaS-platform en kan meegroeien over seats, integraties, ticketvolume en governed engineering workflows. De uitrol start met begeleide onboarding omdat het platform koppelt met gevoelige operationele systemen; approval models, integration health en audit boundaries worden samen met je team gevalideerd, en gebruik wordt van daaruit uitgebreid.",
    },
  },
  related: {
    agent: {
      label: "FC Agent — private infrastructuur",
      description: "Uitsluitend uitgaande connectiviteit voor private Kubernetes, Argo, Jenkins en GitLab.",
    },
    validation: {
      label: "Change-validatie",
      description: "Geïsoleerde Docker-validatie en validatie in je eigen omgeving.",
    },
    deployment: {
      label: "Deployment-governance",
      description: "Hoe een gepauzeerde deployment één vastgelegde beslissing wordt.",
    },
  },
  cta: {
    title: "Securityvragen verdienen echte antwoorden",
    description:
      "De volledige security-documentatie behandelt isolatie, identiteit, de auditketen, incident response en de bewuste grenzen — in detail.",
    primary: "Lees de security-documentatie",
    secondary: "Neem contact op",
  },
};
